import Vue from 'vue'
import VueRouter from 'vue-router'
import CollectionView from '@/components/collection-view'
import SearchResults from '@/components/search-results'
import CollectionEntry from '@/views/collection-entry'
import Store from '@/store/index.js'
import LandingPage from '@/views/library-landing-page.vue'
import LoginPopup from '@/components/login-popup'
import SignUpPopup from '@/components/sign-up-popup'
import UserTerms from '@/components/user-terms'
import PasswordResetPopup from '@/components/password-reset-popup'
import setNewPassword from '@/components/set-new-password'
import baseLibrary from '@/views/base-library'
import Uploads from '@/views/uploads'
import version from '@/components/version'
import UnauthorizedPopup from '@/components/unauthorized-popup.vue'
import SuccessResponse from '@/components/success-response'
import contentApproval from '@/views/content-approval.vue'
import staffListPanel from '@/views/staff-list-panel'
import config from '@/config'
import { axiosInstance } from '@/axios'
import { getClasseUnitUrl } from '@/utilityFunctions'
import staffListUpload from '@/views/staff-list-upload'
import { basePanelsRoute } from '@/constants'

Vue.use(VueRouter)

function setSelectedEntryOrigin (from, next) {
  const origin = from.name === 'CollectionView' || from.name === 'searchResult' || !from.name ? 'baseLibrary' : from.name
  Store.dispatch('setEntryOrigin', origin)
  next()
}

function getCookie (cname) {
  const name = cname + '='
  const decodedCookie = decodeURIComponent(document.cookie)
  const ca = decodedCookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') {
      c = c.substring(1)
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length)
    }
  }
  return ''
}

function addEntryToUnits (to, from, next) {
  if (config.authToken) {
    Store.dispatch('setLoader', true)
    const entryId = to.params.id
    Store.dispatch('getCollectionEntry', entryId).then(async response => {
      const entry = response.data
      let lang = await (axiosInstance.get('library/' + to.params.librarySlug))
      lang = lang.data.lang.split('_')[0]
      if (entry.object.content_type === 'LEGACY_ENGINE') {
        Store.dispatch('addToMyCollection', entryId).then((response) => {
          location.replace(getClasseUnitUrl(response.data.unit_id, lang) + '&openSharePop=true')
        })
      } else {
        Store.dispatch('addExternalContentToMyCollection', { contentId: entryId, entryObj: entry }).then((response) => {
          location.replace(getClasseUnitUrl(response.data.unit_id, lang) + '&openSharePop=true')
        })
      }
    })
  } else {
    next()
  }
}

function checkAuth (to, from, next) {
  // get jwt from the cookies
  const jwt = getCookie('token')
  if (jwt !== '') {
    localStorage.setItem('jwt', jwt)
    document.cookie = 'token=; path=/'
  }
  Store.dispatch('getUserProfile')
  const nextUrl = null
  const params = { librarySlug: to.params.librarySlug }

  Store.dispatch('goCollectionEntryDirect').then((isSpecificUrl) => {
    // If we haven't a specific URL, go to the base page.
    if (!isSpecificUrl) {
      next({
        name: 'baseLibrary'
      })
    }
  })
  next({
    name: nextUrl,
    params: params,
    replace: true
  })
}

const routes = [

  {
    path: '/version',
    name: 'Version',
    component: version
  },
  {
    path: '/:librarySlug/',
    component: { render: (c) => c('router-view') },
    redirect: '/:librarySlug/home-page',
    children: [{
      path: 'home-page/',
      name: 'homePage',
      component: LandingPage,
      children: [
        {
          path: 'logIn',
          name: 'logIn',
          component: LoginPopup
        },
        {
          path: 'signUp',
          name: 'signUp',
          component: SignUpPopup
        },
        {
          path: 'terms-of-use',
          name: 'termsOfUse',
          component: UserTerms
        },
        {
          path: 'reset-password',
          name: 'resetPassword',
          component: PasswordResetPopup
        },
        {
          path: 'set-new-password/:uid?/:token?',
          name: 'setNewPassword',
          component: setNewPassword
        }
      ]
    },
    {
      path: 'check-permissions',
      name: 'checkPermissions',
      beforeEnter: (to, from, next) => {
        checkAuth(to, from, next)
      }
    },
    {
      path: 'base/',
      name: 'baseLibrary',
      component: baseLibrary,
      beforeEnter (to, from, next) {
        Store.dispatch('setPanelRoute', basePanelsRoute.baseLibrary)
        next()
      },
      redirect: {
        name: 'CollectionView'
      },
      children: [
        {
          path: '',
          name: 'CollectionView',
          component: CollectionView
        },
        {
          path: 'search-result',
          query: {
            searchText: {
              required: true
            }
          },
          name: 'searchResult',
          component: SearchResults
        }
      ]
    },
    {
      path: 'collection-entry/add-to-units/:id',
      name: 'addEntryToUnits',
      beforeEnter: (to, from, next) => {
        addEntryToUnits(to, from, next)
      }
    },
    {
      path: 'collection-entry/:id/',
      name: 'collectionEntry',
      component: CollectionEntry
    },
    {
      path: 'collection-entry/success-add-to-my-area/:unitId',
      name: 'successAddToMyArea',
      component: SuccessResponse
    },
    {
      path: 'collection-entry/:id/edit/:type',
      name: 'edit-collection',
      component: Uploads,
      beforeEnter: (to, from, next) => {
        const origin = 'collectionEntry'
        Store.dispatch('setEntryOrigin', origin)
        next()
      }
    },
    {
      path: 'uploads/:type',
      name: 'uploads',
      component: Uploads,
      beforeEnter: (to, from, next) => {
        setSelectedEntryOrigin(from, next)
      }
    },
    {
      path: 'unauthorized-user',
      name: 'unauthorizedUser',
      component: UnauthorizedPopup
    },
    {
      path: 'content-approval',
      name: 'contentApproval',
      component: contentApproval,
      beforeEnter (to, from, next) {
        Store.dispatch('setPanelRoute', basePanelsRoute.contentApproval)
        next()
      }
    },
    {
      path: 'content-approval/collection-entry/:id/',
      name: 'contentApprovalCollectionEntry',
      component: CollectionEntry,
      beforeEnter (to, from, next) {
        Store.dispatch('setPanelRoute', basePanelsRoute.contentApproval)
        next()
      }
    },
    {
      path: 'staff-list',
      name: 'staffList',
      component: staffListPanel,
      beforeEnter (to, from, next) {
        Store.dispatch('setPanelRoute', basePanelsRoute.staffList)
        next()
      }
    },
    {
      path: 'staff-list/add-members',
      name: 'staffListUpload',
      component: staffListUpload,
      beforeEnter (to, from, next) {
        Store.dispatch('setPanelRoute', basePanelsRoute.staffList)
        next()
      }
    }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

export default router
